import { useState, useEffect, useMemo, useRef } from 'react'
import { updateAircraft, createAircraft } from '../lib/api'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { AIRCRAFT_TYPES as FALLBACK_TYPES } from '../lib/constants'

function CustomerDropdown({ value, onChange, customers, onAddNew }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)
  const inputRef = useRef(null)

  const selected = customers.find(c => c.id === value)

  const filtered = useMemo(() => {
    if (!search) return customers.slice(0, 40)
    const q = search.toLowerCase()
    return customers.filter(c => c.name.toLowerCase().includes(q)).slice(0, 20)
  }, [customers, search])

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleOpen = () => {
    setOpen(true)
    setSearch('')
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleSelect = (id) => {
    onChange(id)
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 bg-white text-left"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? selected.name : 'Select customer…'}
        </span>
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 flex flex-col max-h-52">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customers…"
              className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-red/20"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {value && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 italic"
              >
                Clear selection
              </button>
            )}
            {filtered.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelect(c.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${c.id === value ? 'bg-brand-red/10 text-brand-red font-medium' : 'text-gray-800'}`}
              >
                {c.name}
                {c.company && <span className="text-gray-400 ml-1.5 font-normal">· {c.company}</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No customers match</div>
            )}
          </div>
          <div className="border-t border-gray-100 p-1.5">
            <button
              type="button"
              onClick={() => { setOpen(false); onAddNew(search) }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-brand-red hover:bg-brand-red/10 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {search ? `Add "${search}" as new customer` : 'Add new customer'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AircraftModal({ aircraft, onClose, onSaved, existingAircraft = [], prefillCustomerName = '', inline = false }) {
  const { profile } = useAuth()
  const AIRCRAFT_TYPES = profile?.orgs?.settings?.aircraftTypes ?? FALLBACK_TYPES

  const [form, setForm] = useState({
    registration: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    aircraft_type: 'Jet',
    customer: '',
  })
  const [saving, setSaving]           = useState(false)
  const [mfrSearch, setMfrSearch]     = useState('')
  const [modelSearch, setModelSearch] = useState('')
  const [showMfrDropdown, setShowMfrDropdown]     = useState(false)
  const [showModelDropdown, setShowModelDropdown] = useState(false)

  // Customer state
  const [customers, setCustomers]         = useState([])
  const [selectedCustomerId, setSelectedCustomerId] = useState(null)
  const [addingCustomer, setAddingCustomer]   = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [savingCustomer, setSavingCustomer]   = useState(false)

  useEffect(() => {
    if (aircraft) {
      setForm({ ...aircraft })
      setMfrSearch(aircraft.manufacturer || '')
      setModelSearch(aircraft.model || '')
    }
  }, [aircraft])

  // Fetch customers and pre-select based on aircraft.customer name or prefillCustomerName
  useEffect(() => {
    supabase.from('customers').select('id, name, company').order('name').then(({ data }) => {
      const list = data || []
      setCustomers(list)
      const nameToMatch = aircraft?.customer || prefillCustomerName
      if (nameToMatch) {
        const match = list.find(c => c.name.toLowerCase() === nameToMatch.toLowerCase())
        if (match) setSelectedCustomerId(match.id)
      }
    })
  }, [aircraft?.customer, prefillCustomerName])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const manufacturers = useMemo(() => {
    const all = existingAircraft.map(a => a.manufacturer).filter(Boolean)
    return [...new Set(all)].sort()
  }, [existingAircraft])

  const models = useMemo(() => {
    const all = existingAircraft
      .filter(a => !form.manufacturer || a.manufacturer === form.manufacturer)
      .map(a => a.model)
      .filter(Boolean)
    return [...new Set(all)].sort()
  }, [existingAircraft, form.manufacturer])

  const filteredMfrs   = manufacturers.filter(m => m.toLowerCase().includes(mfrSearch.toLowerCase()))
  const filteredModels = models.filter(m => m.toLowerCase().includes(modelSearch.toLowerCase()))

  const handleMfrSelect = (mfr) => {
    set('manufacturer', mfr)
    setMfrSearch(mfr)
    setShowMfrDropdown(false)
    const match = existingAircraft.find(a => a.manufacturer === mfr && a.aircraft_type)
    if (match) set('aircraft_type', match.aircraft_type)
  }

  const handleModelSelect = (model) => {
    set('model', model)
    setModelSearch(model)
    setShowModelDropdown(false)
    const match = existingAircraft.find(a => a.model === model && a.aircraft_type)
    if (match) {
      set('aircraft_type', match.aircraft_type)
      if (!form.manufacturer && match.manufacturer) {
        set('manufacturer', match.manufacturer)
        setMfrSearch(match.manufacturer)
      }
    }
  }

  const handleAddNewCustomer = async () => {
    if (!newCustomerName.trim()) return
    setSavingCustomer(true)
    const { data, error } = await supabase
      .from('customers')
      .insert({ name: newCustomerName.trim() })
      .select('id, name, company')
      .single()
    if (!error && data) {
      setCustomers(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
      setSelectedCustomerId(data.id)
    }
    setSavingCustomer(false)
    setAddingCustomer(false)
    setNewCustomerName('')
  }

  const save = async () => {
    if (!form.registration) return
    setSaving(true)

    const selectedCustomer = customers.find(c => c.id === selectedCustomerId)
    const customerName = selectedCustomer?.name || form.customer || prefillCustomerName || ''
    const formToSave = { ...form, customer: customerName }

    let savedAircraft = null
    if (aircraft) {
      await updateAircraft(aircraft.id, formToSave)
      savedAircraft = { ...formToSave, id: aircraft.id }
      if (selectedCustomerId) {
        await supabase.from('customer_aircraft')
          .upsert({ customer_id: selectedCustomerId, aircraft_id: aircraft.id }, { onConflict: 'customer_id,aircraft_id' })
      }
      // Sync customer_name to all active projects linked to this aircraft
      if (customerName) {
        await supabase
          .from('projects')
          .update({ customer_name: customerName })
          .eq('aircraft_id', aircraft.id)
          .neq('status', 'Complete')
      }
    } else {
      const { data, error } = await createAircraft(formToSave)
      if (!error && data) {
        savedAircraft = data
        if (selectedCustomerId) {
          await supabase.from('customer_aircraft')
            .insert({ customer_id: selectedCustomerId, aircraft_id: data.id })
            .then(() => {})
        }
      }
    }

    setSaving(false)
    onSaved(savedAircraft)
    onClose()
  }

  const inner = (
    <div className={inline ? 'bg-white rounded-xl border border-gray-200' : 'bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto'}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">{aircraft ? 'Edit Aircraft' : 'Add Aircraft'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Registration */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Registration *</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              placeholder="e.g. N12345"
              value={form.registration}
              onChange={e => set('registration', e.target.value.toUpperCase())}
            />
          </div>

          {/* Manufacturer */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Manufacturer</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              placeholder="e.g. Cessna"
              value={mfrSearch}
              onChange={e => { setMfrSearch(e.target.value); set('manufacturer', e.target.value); setShowMfrDropdown(true) }}
              onFocus={() => setShowMfrDropdown(true)}
              onBlur={() => setTimeout(() => setShowMfrDropdown(false), 150)}
            />
            {showMfrDropdown && filteredMfrs.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-36 overflow-y-auto">
                {filteredMfrs.map(m => (
                  <button key={m} type="button" onMouseDown={() => handleMfrSelect(m)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors">{m}</button>
                ))}
              </div>
            )}
          </div>

          {/* Model */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Model
              {form.manufacturer && models.length > 0 && (
                <span className="ml-1 text-gray-400 font-normal">({models.length} known for {form.manufacturer})</span>
              )}
            </label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              placeholder="e.g. 172"
              value={modelSearch}
              onChange={e => { setModelSearch(e.target.value); set('model', e.target.value); setShowModelDropdown(true) }}
              onFocus={() => setShowModelDropdown(true)}
              onBlur={() => setTimeout(() => setShowModelDropdown(false), 150)}
            />
            {showModelDropdown && filteredModels.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-36 overflow-y-auto">
                {filteredModels.map(m => (
                  <button key={m} type="button" onMouseDown={() => handleModelSelect(m)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors">{m}</button>
                ))}
              </div>
            )}
          </div>

          {/* Customer — searchable dropdown */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
            {prefillCustomerName && !selectedCustomerId ? (
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                <span className="text-sm text-gray-700 flex-1">{prefillCustomerName}</span>
                <span className="text-xs text-gray-400">will be linked on save</span>
              </div>
            ) : addingCustomer ? (
              <div className="flex gap-2">
                <input
                  autoFocus
                  type="text"
                  value={newCustomerName}
                  onChange={e => setNewCustomerName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewCustomer() } if (e.key === 'Escape') { setAddingCustomer(false); setNewCustomerName('') } }}
                  placeholder="Customer name"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                />
                <button
                  type="button"
                  onClick={handleAddNewCustomer}
                  disabled={savingCustomer || !newCustomerName.trim()}
                  className="px-3 py-2 text-sm font-medium bg-brand-red text-white rounded-lg hover:bg-brand-red-dark disabled:opacity-50"
                >
                  {savingCustomer ? '…' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => { setAddingCustomer(false); setNewCustomerName('') }}
                  className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <CustomerDropdown
                value={selectedCustomerId}
                onChange={setSelectedCustomerId}
                customers={customers}
                onAddNew={(prefill) => { setNewCustomerName(prefill); setAddingCustomer(true) }}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Serial Number */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Serial Number</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                placeholder="e.g. 12345"
                value={form.serial_number || ''}
                onChange={e => set('serial_number', e.target.value)}
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={form.aircraft_type}
                onChange={e => set('aircraft_type', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              >
                {AIRCRAFT_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !form.registration}
            className="text-sm font-medium bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
          >
            {saving ? 'Saving...' : aircraft ? 'Save Changes' : 'Add Aircraft'}
          </button>
        </div>
      </div>
  )

  if (inline) return inner

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      {inner}
    </div>
  )
}
